import * as React from 'react';
import TreeItem, { TreeItemContentProps, TreeItemProps, useTreeItem } from '@mui/lab/TreeItem';
import clsx from 'clsx';
import Typography from '@mui/material/Typography';
import { Box, styled } from '@mui/material';
import { TextSnippet } from '@mui/icons-material';
import { MultiplicityEnum } from '../../../../enums/Multiplicity.enum';
import { TemplateEditTreeItemMenu } from './TemplateEditTreeItemMenu';
import { useState } from 'react';
import { messages } from '../../../../i18n/localization';
import { useIntl } from 'react-intl';

interface CustomTreeItemProps extends TreeItemProps {
    hasValue?: boolean;
    customOnSelect: () => void;
    multiplicity: MultiplicityEnum | undefined;
    onDuplicate: (nodeId: string) => void;
    onDelete: (nodeId: string) => void;
    onRestore: (nodeId: string) => void;
    getNumberOfElementsWithSameSemanticId: (semanticId: string | undefined) => number;
    semanticId: string | undefined;
    isParentAboutToBeDeleted: boolean | undefined;
    isAboutToBeDeleted: boolean | undefined;
}
interface CustomTreeItemContentProps extends TreeItemContentProps {
    hasValue?: boolean;
    customOnSelect: () => void;
    multiplicity: MultiplicityEnum | undefined;
    onDuplicate: (nodeId: string) => void;
    onDelete: (nodeId: string) => void;
    onRestore: (nodeId: string) => void;
    getNumberOfElementsWithSameSemanticId: (semanticId: string | undefined) => number;
    semanticId: string | undefined;
    isParentAboutToBeDeleted: boolean | undefined;
    isAboutToBeDeleted: boolean | undefined;
}

const StyledTreeItem = styled(TreeItem)(({ theme }) => ({
    '.MuiTreeItem-content': {
        userSelect: 'none',
        margin: 0,
        '&.Mui-focused': {
            backgroundColor: 'transparent',
        },
        '&.Mui-focused:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        '&.Mui-focused.Mui-selected': {
            backgroundColor: theme.palette.action.selected,
        },
    },
}));

const CustomContent = React.forwardRef(function CustomContent(props: CustomTreeItemContentProps, ref) {
    const {
        classes,
        className,
        label,
        nodeId,
        icon: iconProp,
        expansionIcon,
        displayIcon,
        hasValue,
        customOnSelect,
        multiplicity,
        getNumberOfElementsWithSameSemanticId,
        semanticId,
    } = props;

    const { disabled, expanded, selected, focused, handleExpansion, handleSelection, preventSelection } =
        useTreeItem(nodeId);

    const icon = iconProp || expansionIcon || displayIcon;

    const [isAboutToBeDeleted, setIsAboutToBeDeleted] = useState(false);

    React.useEffect(() => {
        setIsAboutToBeDeleted(false);
    }, [label, semanticId]);

    React.useEffect(() => {
        if (props.isAboutToBeDeleted) {
            setIsAboutToBeDeleted(true);
        }
    }, [props.isAboutToBeDeleted]);

    React.useEffect(() => {
        // Trigger the customOnSelect initially for the first selected element, so the edit fields are displayed
        if (selected) {
            customOnSelect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        preventSelection(event);
    };

    const handleExpansionClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        handleExpansion(event);
    };

    const handleSelectionClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        customOnSelect();
        handleSelection(event);
    };

    const onDelete = () => {
        props.onDelete(nodeId);
        setIsAboutToBeDeleted(true);
    };

    const onRestore = () => {
        props.onRestore(nodeId);
        setIsAboutToBeDeleted(false);
    };

    const intl = useIntl();

    return (
        <Box
            className={clsx(className, classes.root, {
                [classes.expanded]: expanded,
                [classes.selected]: selected,
                [classes.focused]: focused,
                [classes.disabled]: disabled,
            })}
            onMouseDown={handleMouseDown}
            ref={ref as React.Ref<HTMLDivElement>}
        >
            <Box onClick={handleExpansionClick} className={classes.iconContainer} sx={{ py: 1 }}>
                {icon}
            </Box>
            <Box onClick={handleSelectionClick} display="flex" sx={{ width: '100%' }}>
                <Box sx={{ py: 1, pr: 1, display: 'flex' }} onClick={handleSelectionClick}>
                    {isAboutToBeDeleted || props.isParentAboutToBeDeleted ? (
                        <>
                            <Typography component="div" className={classes.label} sx={{ color: 'text.disabled' }}>
                                {label + ' (' + intl.formatMessage(messages.mnestix.deleted) + ')'}
                            </Typography>
                            {hasValue && <TextSnippet fontSize="small" sx={{ color: 'text.disabled', ml: '3px' }} />}
                        </>
                    ) : (
                        <>
                            <Typography component="div" className={classes.label}>
                                {label}
                            </Typography>
                            {hasValue && <TextSnippet fontSize="small" sx={{ color: 'text.secondary', ml: '3px' }} />}
                        </>
                    )}
                </Box>
            </Box>
            <TemplateEditTreeItemMenu
                elementMultiplicity={multiplicity}
                numberOfThisElement={getNumberOfElementsWithSameSemanticId(semanticId)}
                onDuplicate={props.onDuplicate}
                onDelete={onDelete}
                onRestore={onRestore}
                nodeId={nodeId}
                isElementAboutToBeDeleted={props.isAboutToBeDeleted || isAboutToBeDeleted}
                isParentAboutToBeDeleted={props.isParentAboutToBeDeleted}
            />
        </Box>
    );
});

export const TemplateEditTreeItem = (props: CustomTreeItemProps) => {
    const {
        customOnSelect,
        hasValue,
        multiplicity,
        onDuplicate,
        onDelete,
        onRestore,
        getNumberOfElementsWithSameSemanticId,
        semanticId,
        isParentAboutToBeDeleted,
        isAboutToBeDeleted,
        ...other
    } = props;
    return (
        <StyledTreeItem
            ContentComponent={CustomContent}
            ContentProps={{
                customOnSelect,
                hasValue,
                multiplicity,
                onDuplicate,
                onDelete,
                onRestore,
                getNumberOfElementsWithSameSemanticId,
                semanticId,
                isParentAboutToBeDeleted,
                isAboutToBeDeleted,
            }}
            {...other}
        />
    );
};
